import { Link } from 'react-router-dom'

const SECTIONS = [
  {
    title: 'Vue d\'ensemble',
    icon: '📊',
    href: '/admin',
    items: [
      'KPIs en temps réel : nombre d\'entreprises, prestataires, missions totales et revenus capturés.',
      'Activité récente : dernières missions créées, derniers utilisateurs inscrits.',
      'Point d\'entrée pour naviguer vers toutes les sections de l\'administration.',
    ],
  },
  {
    title: 'Gestion des utilisateurs',
    icon: '👥',
    href: '/admin/utilisateurs',
    items: [
      '<strong>Entreprises</strong> : liste complète avec SIRET, email, date de création et nombre de missions. Possibilité de suspendre un compte.',
      '<strong>Prestataires</strong> : liste avec spécialités, note, géolocalisation active ou non. Peut désactiver un compte ou modifier les spécialités manuellement.',
      '<strong>Centres de formation</strong> : même interface que les entreprises, avec le flag Qualiopi visible. Peut forcer l\'activation du badge Qualiopi après vérification manuelle.',
    ],
  },
  {
    title: 'Supervision des missions',
    icon: '📋',
    href: '/admin/missions',
    items: [
      'Liste de toutes les missions (tous statuts, toutes entreprises) avec filtres par type, statut, date.',
      'Accès au détail d\'une mission : prestataire assigné, montant, géolocalisation, historique des statuts.',
      'Possibilité d\'annuler une mission en cas de fraude ou de litige non résolu.',
    ],
  },
  {
    title: 'Domaines & spécialités',
    icon: '🏷️',
    href: '/admin/types-missions',
    items: [
      'Les <strong>domaines</strong> (Sécurité & sûreté, Événementiel, Formation…) structurent l\'offre et alimentent le bouton « Trouver un expert » de l\'accueil. Créez-en autant que nécessaire, sans déploiement.',
      'Un domaine <strong>★ mis en avant</strong> apparaît parmi les raccourcis de l\'accueil ; l\'ordre des flèches ▲▼ est celui affiché aux visiteurs.',
      'Chaque <strong>spécialité</strong> appartient à un domaine et a un label, une icône, une description et un flag "active". Désactiver (spécialité ou domaine entier) masque sans supprimer ; la suppression est refusée si des prestataires l\'ont cochée.',
      'La <strong>catégorie</strong> (Événement, Formation, Les deux) détermine dans quels formulaires de mission la spécialité est proposée.',
    ],
  },
  {
    title: 'Statistiques',
    icon: '📈',
    href: '/admin/stats',
    items: [
      'Graphiques d\'évolution des inscriptions, missions et revenus sur 12 mois.',
      'Répartition par type de mission (Événement, Formation, ADS, SSIAP, Autre).',
      'Top 10 prestataires par nombre de missions réalisées et top 10 entreprises par volume de dépenses.',
    ],
  },
  {
    title: 'Logs d\'activité',
    icon: '🗒️',
    href: '/admin/logs',
    items: [
      'Historique complet des actions sensibles : connexions, créations de missions, paiements, modifications de compte.',
      'Filtrable par date, utilisateur, type d\'action. Utile pour audits et investigations.',
      'Les logs de déploiement CI/CD (GitHub Actions → VPS) sont également archivés ici.',
    ],
  },
  {
    title: 'Paramètres plateforme',
    icon: '⚙️',
    href: '/admin/parametres',
    items: [
      '<strong>Informations légales</strong> : nom de la société, SIRET, adresse, email de contact — utilisés dans les Mentions légales.',
      '<strong>Commission</strong> : pourcentage prélevé côté entreprise (par défaut 10 % à partir de la 2e mission). Modifiable ici sans redéploiement.',
      '<strong>Rayon par défaut</strong> : rayon de recherche en km appliqué si l\'entreprise ne précise pas le sien à la publication.',
    ],
  },
  {
    title: 'Base de données',
    icon: '🗄️',
    href: '/admin/base-de-donnees',
    items: [
      'Interface de consultation directe des tables Prisma (lecture seule en prod — écriture uniquement via l\'interface admin).',
      'Utile pour vérifier l\'état d\'un enregistrement spécifique sans avoir à SSH sur le VPS.',
      'En cas d\'urgence, les modifications directes en base doivent être réalisées depuis le VPS : `ssh -i ~/.ssh/velink_deploy root@195.35.24.209`.',
    ],
  },
]

const OPS = [
  {
    label: 'Vérifier l\'état des services',
    cmd: 'ssh -i ~/.ssh/velink_deploy root@195.35.24.209\npm2 status',
    desc: 'Affiche l\'état de velink-web (port 3010), velink-api (port 3011) et du deploy-listener (port 3012).',
  },
  {
    label: 'Redémarrer le frontend',
    cmd: 'pm2 restart velink-web',
    desc: 'Redémarrage sans coupure (PM2 fork mode). Le site reste accessible pendant le restart.',
  },
  {
    label: 'Voir les logs en live',
    cmd: 'pm2 logs velink-api --lines 100',
    desc: 'Remplacez velink-api par velink-web ou velink-deploy selon le service concerné.',
  },
  {
    label: 'Déclencher un déploiement manuel',
    cmd: 'cd /var/www/velink && git pull && cd apps/web && npm run build && pm2 reload all',
    desc: 'À utiliser si le webhook CI/CD échoue. Le build dure ~2 minutes.',
  },
]

export default function GuideAdminPage() {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-danger/10 border border-danger/20 text-danger text-xs font-medium mb-4">
          Guide administrateur — Accès restreint
        </div>
        <h1 className="text-2xl font-bold text-text-primary mb-2">Administration Velink</h1>
        <p className="text-text-secondary text-sm leading-relaxed max-w-2xl">
          Ce guide décrit le rôle de chaque section de l'interface d'administration, les opérations courantes
          et les procédures d'urgence sur le VPS.
        </p>
      </div>

      {/* Warning */}
      <div className="glass-card p-4 rounded-xl border border-danger/25 bg-danger/[0.04] flex items-start gap-3">
        <span className="text-danger text-lg flex-shrink-0">⚠️</span>
        <div>
          <p className="text-danger font-semibold text-sm mb-1">Accès admin sensible</p>
          <p className="text-text-secondary text-sm">Ne partagez jamais vos identifiants admin. Toutes les actions admin sont loguées. En cas de compromission, révoquez immédiatement le JWT depuis les paramètres et changez le mot de passe.</p>
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-4">
        {SECTIONS.map((section) => (
          <div key={section.title} className="glass-card rounded-xl overflow-hidden">
            <div className="flex items-center gap-4 p-5">
              <div className="w-10 h-10 rounded-xl bg-danger/15 border border-danger/25 flex items-center justify-center flex-shrink-0">
                <span className="text-lg">{section.icon}</span>
              </div>
              <div className="flex-1">
                <h2 className="text-text-primary font-semibold">{section.title}</h2>
              </div>
              <Link
                to={section.href}
                className="glass-btn px-3 py-1.5 text-xs rounded-lg flex-shrink-0"
              >
                Accéder →
              </Link>
            </div>
            <div className="px-5 pb-5 border-t border-white/[0.06] pt-4">
              <ul className="space-y-2">
                {section.items.map((line, i) => (
                  <li key={i} className="flex items-start gap-2 text-text-secondary text-sm leading-relaxed">
                    <span className="text-danger mt-0.5 flex-shrink-0">→</span>
                    <span dangerouslySetInnerHTML={{ __html: line }} />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {/* VPS Ops */}
      <div>
        <h2 className="text-text-primary font-semibold mb-4 flex items-center gap-2">
          <span>🖥️</span> Opérations VPS (195.35.24.209)
        </h2>
        <div className="space-y-3">
          {OPS.map((op) => (
            <div key={op.label} className="glass-card p-5 rounded-xl">
              <p className="text-text-primary font-medium text-sm mb-2">{op.label}</p>
              <pre className="bg-black/30 rounded-lg px-4 py-3 text-xs font-mono text-success mb-2 overflow-x-auto whitespace-pre-wrap">{op.cmd}</pre>
              <p className="text-text-muted text-xs">{op.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Infra reminder */}
      <div className="glass-card p-5 rounded-xl">
        <h2 className="text-text-primary font-semibold mb-4 flex items-center gap-2">
          <span>🔑</span> Rappels infrastructure
        </h2>
        <div className="grid grid-cols-2 gap-3 text-sm">
          {[
            { label: 'Frontend (web)', value: 'PM2 velink-web — port 3010' },
            { label: 'Backend (api)', value: 'PM2 velink-api — port 3011' },
            { label: 'Deploy listener', value: 'systemd velink-deploy — port 3012' },
            { label: 'Base de données', value: 'PostgreSQL 15 — 127.0.0.1:5432' },
            { label: 'SSL expire le', value: '31 août 2026 (auto-renouvellement)' },
            { label: 'Repo GitHub', value: 'easymilka/velink (privé)' },
          ].map((r) => (
            <div key={r.label} className="glass-card p-3 rounded-xl">
              <p className="text-text-muted text-xs mb-0.5">{r.label}</p>
              <p className="text-text-primary text-xs font-mono">{r.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Contact */}
      <div className="glass-card p-5 rounded-xl flex items-start gap-4">
        <span className="text-2xl">🆘</span>
        <div>
          <p className="text-text-primary font-semibold text-sm mb-1">Incident critique ?</p>
          <p className="text-text-secondary text-sm">
            Contactez Jeremy Roger directement : <a href="mailto:jeremy.roger25@gmail.com" className="text-accent hover:underline">jeremy.roger25@gmail.com</a>.
            Pour les pannes de paiement, consultez le dashboard Stripe et les webhooks PayPal avant de contacter le support.
          </p>
        </div>
      </div>
    </div>
  )
}
